import { useDispatch, useSelector } from "react-redux";
import { closeGalleryModal } from "../../../redux/reducer/modal.reducer";
import Row from "../../../components/element/row";
import Col from "../../../components/element/col";
import FullModal from "../../../components/modal/full.modal";
import { MDBModalBody } from "mdb-react-ui-kit";
import CommonModalHeader from "../../../components/common/common.modal.header";
import { setGalleryTab } from "../../../redux/reducer/product.reducer";
import ImageComponent from "../../../components/element/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pannellum } from "pannellum-react";
import { useEffect, useRef, useState } from "react";
import {
  openFullSizeGallery,
  setFullSizeGallery,
} from "../../../redux/reducer/common.reducer";
import Icon from "../../../components/element/icon";
import { gallerySlide, mediaType } from "../../../helpers/gallery.media";
import { useTranslation } from "react-i18next";

export default function GalleryModal() {
  const swiperRef = useRef(null);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isOpenGalleryModal } = useSelector((state) => state.modal);
  const { productMedia, galleryTab } = useSelector((state) => state.product);

  const [panoramaHeight, setPanoramaHeight] = useState(0);
  const [modalReady, setModalReady] = useState(false);

  const handleCloseGallery = () => {
    dispatch(closeGalleryModal());
  };

  const thumbnailList = [
    {
      title: "All",
      key: "all_image",
    },
    {
      title: "Exterior",
      key: "exterior_image",
    },
    {
      title: "Interior",
      key: "interior_image",
    },
    {
      title: "Imperfection",
      key: "imperfection_image",
    },
    {
      title: "360 Degree",
      key: "panorama_image",
    },
    {
      title: "Video",
      key: "video",
    },
  ];

  const buildRows = (list = []) => {
    const rows = [];
    let i = 0;
    let takeTwo = false; // pattern: [1], [2], [1]...

    const withIndex = (obj, idx) => {
      const { index: _ignore, ...rest } = obj || {};
      return { ...rest, index: idx };
    };

    while (i < list.length) {
      if (takeTwo && i + 1 < list.length) {
        rows.push([withIndex(list[i], i), withIndex(list[i + 1], i + 1)]);
        i += 2;
      } else {
        rows.push([withIndex(list[i], i)]);
        i += 1;
      }
      takeTwo = !takeTwo;
    }

    return rows;
  };

  const handleOpenGallery = (galleryTab, clickedIndex = 0) => {
    const currentList = productMedia?.[galleryTab] ?? [];

    const otherList = Object.entries(productMedia ?? {})
      .filter(
        ([key]) =>
          key !== galleryTab &&
          key !== "all_image" &&
          key !== "panorama_image" &&
          key !== "car_report" &&
          key !== "inspection_report",
      )
      .flatMap(([, list]) => list ?? []);

    const mergedList = [...currentList, ...otherList];

    const gallery = mergedList.filter(media => media?.url).map(gallerySlide);

    if (!gallery.length) return;

    // ✅ clickedIndex is correct because currentList is at the front
    const sortedGallery = [
      ...gallery.slice(clickedIndex),
      ...gallery.slice(0, clickedIndex),
    ];

    dispatch(setFullSizeGallery(sortedGallery));
    dispatch(openFullSizeGallery());
  };

  useEffect(() => {
    if (
      galleryTab === "panorama_image" &&
      productMedia[galleryTab]?.length > 0
    ) {
      const img = new Image();
      img.src = productMedia[galleryTab][0].url;

      img.onload = () => {
        setPanoramaHeight(Math.min(img.naturalHeight, 500));

        // const panoramaContainer = document.querySelector(".pnlm-container");
        // if (panoramaContainer) {
        //   panoramaContainer.style.height = `${panoramaHeight}px`;
        // }
      };

      img.onerror = () => {
        console.error("Failed to load panorama image");
      };
    }
  }, [productMedia, galleryTab]);

  useEffect(() => {
    setModalReady(true);

    if (modalReady) {
      const swiper = swiperRef.current;

      if (!swiper) return;

      const idx = thumbnailList.findIndex((t) => t.key === galleryTab);
      if (idx < 0) return;

      swiper.slideTo(idx, 1000);
    }
  }, [galleryTab, modalReady]);

  // `sourceIndex` is the file's place in the tab's own list, the order the
  // full-size gallery opens in.
  const tabMedia = (productMedia?.[galleryTab] ?? [])
    .map((media, sourceIndex) => ({ ...media, sourceIndex }))
    .filter((media) => media?.url);

  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (
      !Array.isArray(productMedia?.panorama_image) ||
      !productMedia.panorama_image?.[0]?.api_url
    ) {
      return;
    }

    let objectUrl;

    fetch(productMedia.panorama_image[0].api_url)
      .then((res) => res.blob())
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => {});

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [productMedia?.panorama_image]);

  return (
    <FullModal
      staticBackdrop={true}
      show={isOpenGalleryModal}
      close={handleCloseGallery}
      screenSize="xl"
      className="gallery-modal"
      content={
        <MDBModalBody>
          <CommonModalHeader
            onBack={() => handleCloseGallery()}
            title="Gallery"
          />
          <Col className="_col gallery-info">
            <Swiper
              touchStartPreventDefault={false}
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              spaceBetween={10}
              slidesPerView="auto"
              grabCursor={true}
              className="w-100 py-3 px-4"
            >
              {thumbnailList.map(
                (thumbnail, thumbnailIndex) =>
                  productMedia[thumbnail.key]?.length > 0 && (
                    <SwiperSlide
                      key={thumbnailIndex}
                      onClick={() => {
                        dispatch(setGalleryTab(thumbnail.key));
                      }}
                    >
                      <Col
                        className={`_col thumbnail-item ${
                          galleryTab === thumbnail.key ? "active" : ""
                        } pointer`}
                      >
                        {thumbnail.key === "video" ? (
                          <video
                            src={productMedia[thumbnail.key]?.[0]?.url}
                            className="_video cover"
                          />
                        ) : (
                          <ImageComponent
                            src={productMedia[thumbnail.key]?.[0]?.url}
                            alt=""
                            className="_img thumbnail"
                          />
                        )}
                        <p className="_label thumbnail-title">
                          {t(thumbnail.title)}{" "}
                          {thumbnail.key !== "video" &&
                            thumbnail.key !== "panorama_image" &&
                            `(${productMedia[thumbnail.key].length})`}
                        </p>
                      </Col>
                    </SwiperSlide>
                  ),
              )}
            </Swiper>
            {galleryTab === "video"
              ? productMedia[galleryTab]?.length > 0 && (
                  <Col
                    className="h-100 flex-col justify-center items-center"
                    onClick={() => handleOpenGallery(galleryTab, 0)}
                  >
                    <video
                      src={productMedia[galleryTab]?.[0]?.url}
                      autoPlay
                      controls
                      // muted
                      className="_video"
                    />
                  </Col>
                )
              : galleryTab === "panorama_image"
                ? productMedia[galleryTab]?.length > 0 &&
                  blobUrl && (
                    <>
                      <Pannellum
                        width="100%"
                        height={`${panoramaHeight}px`}
                        image={blobUrl}
                        // "http://localhost:3000/360.jpg"
                        //   pitch={-10}
                        yaw={360}
                        //   hfov={100}
                        autoLoad
                        mouseZoom={false}
                        // showFullscreenCtrl={false}
                        // showZoomCtrl={false}
                        //   onLoad={() => {}}
                      />
                    </>
                  )
                : tabMedia.length > 0 && (
                    <Row className="_row gallery-tab">
                      {buildRows(tabMedia).map((row, rowIndex) => (
                        <Row key={rowIndex} className="flex flex-nowrap gap-3">
                          {row.map((media) =>
                            mediaType(media) === "video" ? (
                              <button
                                type="button"
                                key={media.index}
                                className="gallery-file"
                                onClick={() =>
                                  handleOpenGallery(
                                    galleryTab,
                                    media.sourceIndex,
                                  )
                                }
                              >
                                <Icon icon="solar:play-circle-linear" />
                                <span>{media.display_name || t("Video")}</span>
                              </button>
                            ) : (
                              <ImageComponent
                                src={media.url}
                                key={media.index}
                                className="_img gallery pointer"
                                onClick={() =>
                                  handleOpenGallery(
                                    galleryTab,
                                    media.sourceIndex,
                                  )
                                }
                              />
                            ),
                          )}
                        </Row>
                      ))}
                    </Row>
                  )}
          </Col>
        </MDBModalBody>
      }
    />
  );
}
